import { useEffect, useRef, useState } from "react"
import { updateQrCode, saveContactPage, textFieldErrors, ApiError } from "../lib/api"
import { validateDestinationUrl } from "../lib/urls"
import useContactPage from "../hooks/useContactPage"
import ContactPageFields, { cleanContactPage, validateContactPage, contactPageErrorsFromServer } from "./ContactPageFields"
import ContactPageRemove from "./ContactPageRemove"
import ColorPicker from "./ColorPicker"
import GradientPicker from "./GradientPicker"
import FrameSelect from "./FrameSelect"
import ShapeSelect from "./ShapeSelect"
import LogoUpload from "./LogoUpload"
import LogoSizeSelect from "./LogoSizeSelect"
import LivePreview from "./LivePreview"
import TextFields from "./TextFields"
import ReCheckLink from "./ReCheckLink"
import MailtoSuggestion from "./MailtoSuggestion"
import DestinationInput from "./DestinationInput"

// Edit a code's label, destination, and styling — without ever touching the
// short link (the token the QR encodes, printed on physical surfaces, must keep
// working forever). The left column is fixed: a live preview that re-renders
// with the chosen colors/frame but keeps encoding tgo.sh/:token, plus the
// short-link block that makes that immutability explicit. The right column is
// editable. Everything stays modal-local until Save, which commits label +
// destination + design in one PATCH; Cancel/Esc/scrim discards.
//
// Built on the native <dialog> (like QrPreviewModal): Esc, the backdrop scrim,
// focus-trapping, and focus-restore-to-opener all come for free.
//
// A hosted contact page (#332): the Contact page kind swaps the destination
// field for the page's fields (saved with PUT .../contact_page, a destination
// edit like any other), picking Website or Email repoints through the usual
// PATCH, and "Remove page details" works even where editing is locked.
// `contactPages` offers the kind on a code that has no page.
export default function EditCodeModal({ code, onClose, onSaved, onUpgrade, isPaid = false, contactPages = false }) {
  const dialogRef = useRef(null)
  const [shown, setShown] = useState(false)
  const cp = useContactPage(code)

  const [label, setLabel] = useState(code.label || "")
  // A page code's destination is the page's own address, never typed, so
  // Website starts empty for it.
  const [destinationUrl, setDestinationUrl] = useState(code.contact_page ? "" : code.destination_url)
  const [foregroundColor, setForegroundColor] = useState(code.foreground_color || "")
  const [backgroundColor, setBackgroundColor] = useState(code.background_color || "")
  const [gradient, setGradient] = useState({ gradientColor: code.gradient_color || "", gradientType: code.gradient_type || "" })
  const [frameStyle, setFrameStyle] = useState(code.frame_style || "none")
  // One object, in the builder's shape, so TextFields serves both.
  const [text, setText] = useState({
    headerText: code.header_text || "",
    footerText: code.footer_text || "",
    textWeight: code.text_weight || "bold",
    textErrors: null
  })
  const [moduleShape, setModuleShape] = useState(code.module_shape || "square")
  const [eyeShape, setEyeShape] = useState(code.eye_shape || "square")
  // Logo edits stay modal-local until Save: `logoFile` is a freshly picked File
  // (multipart on save), `removeLogo` clears the persisted one. With neither set,
  // the preview falls back to the code's saved logo (server-side, by token).
  const [logoFile, setLogoFile] = useState(null)
  const [removeLogo, setRemoveLogo] = useState(false)
  const [logoSize, setLogoSize] = useState(code.logo_size || "medium")
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [appealUrl, setAppealUrl] = useState(null)
  const [suggestion, setSuggestion] = useState(null)

  // editable === false means the per-code scan paywall has locked editing for
  // this (free-tier) user. Paid users always serialize editable: true.
  const locked = code.editable === false
  // Destination integrity: the destination is on Google's unsafe list and scans
  // see a review page. The label and design stay editable; the destination
  // doesn't, until a re-check clears it. Not an upgrade prompt — Pro is locked
  // out of this too.
  const underReview = code.under_review === true

  const shortDisplay = (code.short_url || `tgo.sh/${code.token}`).replace(/^https?:\/\//, "")

  const destinationDirty = cp.hosted
    ? (!code.contact_page || cp.dirty)
    : (code.contact_page ? true : destinationUrl !== code.destination_url)
  const designDirty =
    label !== (code.label || "") ||
    foregroundColor !== (code.foreground_color || "") ||
    backgroundColor !== (code.background_color || "") ||
    gradient.gradientColor !== (code.gradient_color || "") ||
    gradient.gradientType !== (code.gradient_type || "") ||
    frameStyle !== (code.frame_style || "none") ||
    text.headerText !== (code.header_text || "") ||
    text.footerText !== (code.footer_text || "") ||
    text.textWeight !== (code.text_weight || "bold") ||
    moduleShape !== (code.module_shape || "square") ||
    eyeShape !== (code.eye_shape || "square") ||
    logoFile !== null ||
    removeLogo ||
    logoSize !== (code.logo_size || "medium")
  const dirty = destinationDirty || designDirty

  // Show the modal on mount; the rAF flip drives the entry pop (opacity +
  // translate) on the inner panel. The native dialog restores focus to the
  // opener when it closes.
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
    const raf = requestAnimationFrame(() => setShown(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  // Lock the page behind the modal: showModal() makes the rest inert but doesn't
  // stop the document from scrolling, so without this the background scrolls
  // while the modal stays put. The modal's own body scrolls internally instead
  // (max-height + overflow-y-auto on the panel below).
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    function handleCancel(e) { e.preventDefault(); onClose() }       // Esc
    function handleClick(e) { if (e.target === dialog) onClose() }   // scrim
    dialog.addEventListener("cancel", handleCancel)
    dialog.addEventListener("click", handleClick)
    return () => {
      dialog.removeEventListener("cancel", handleCancel)
      dialog.removeEventListener("click", handleClick)
    }
  }, [onClose])

  function copyShortLink() {
    navigator.clipboard?.writeText(code.short_url || `tgo.sh/${code.token}`).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  // The design and label, for either kind of destination.
  const designParams = () => ({
    label: label.trim(),
    foregroundColor,
    backgroundColor,
    ...gradient,
    frameStyle,
    headerText: text.headerText,
    footerText: text.footerText,
    textWeight: text.textWeight,
    moduleShape,
    eyeShape,
    logo: logoFile,
    logoSize,
    removeLogo,
  })

  // A hosted contact page: the page's fields go to PUT .../contact_page (a new
  // page, or changed fields), then any design change to the usual PATCH,
  // with no destination in it.
  async function saveHosted() {
    if (!cp.ready) return
    const pageErr = validateContactPage(cp.page)
    cp.setErrors(pageErr)
    if (pageErr) return
    setSaving(true)
    setError(null)
    setAppealUrl(null)
    try {
      let row = null
      if (!code.contact_page || cp.dirty) row = (await saveContactPage(code.token, cleanContactPage(cp.page))).code
      if (designDirty) row = await updateQrCode(code.token, designParams())
      onSaved(row || code)
      onClose()
    } catch (e) {
      setSaving(false)
      if (e instanceof ApiError && e.field === "contact_page") {
        cp.setErrors(contactPageErrorsFromServer(e.details, e.message))
        return
      }
      const textErrors = textFieldErrors(e)
      if (textErrors) {
        setText((t) => ({ ...t, textErrors }))
        return
      }
      setError(e instanceof ApiError && e.message ? e.message : "Couldn't save. Try again.")
      setAppealUrl(e instanceof ApiError ? e.appealUrl : null)
    }
  }

  async function handleSave() {
    if (cp.hosted) return saveHosted()
    // An untouched destination is sent back as-is, so a code saved before a
    // rule tightened (#183) can still be renamed or restyled; the server only
    // re-validates a destination that changes.
    const { normalized, error: message, suggestion: fix } = destinationUrl === code.destination_url
      ? { normalized: destinationUrl, error: null, suggestion: null }
      : validateDestinationUrl(destinationUrl)
    setSuggestion(fix)
    if (message) { setError(message); return }
    setSaving(true)
    setError(null)
    setAppealUrl(null)
    try {
      const updated = await updateQrCode(code.token, {
        label: label.trim(),
        destinationUrl: normalized,
        foregroundColor,
        backgroundColor,
        ...gradient,
        frameStyle,
        headerText: text.headerText,
        footerText: text.footerText,
        textWeight: text.textWeight,
        // Server drops these for non-Pro writers (Entitlements#custom_shapes?),
        // so sending them is harmless even if the client guard is bypassed.
        moduleShape,
        eyeShape,
        // Logo is free on every tier; a File switches the request to multipart.
        logo: logoFile,
        logoSize,
        removeLogo,
      })
      onSaved(updated)
      onClose()
    } catch (e) {
      setSaving(false)
      // A refused character is shown under its field, not as a page error.
      const textErrors = textFieldErrors(e)
      if (textErrors) {
        setText((t) => ({ ...t, textErrors }))
        return
      }
      setError(
        e instanceof ApiError && e.message ? e.message : "Couldn't save. Try again."
      )
      setAppealUrl(e instanceof ApiError ? e.appealUrl : null)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={`edit-modal-title-${code.token}`}
      className="p-0 m-auto bg-transparent backdrop:bg-coal/55 backdrop:backdrop-blur-sm overflow-visible"
    >
      <div
        className={
          // text-ink/dark:text-cloud sets the base text color so inherited
          // labels stay legible in dark mode. flex-col + max-height makes the
          // body (not the page) the scroll region — see the scroll-lock effect.
          "w-[min(92vw,620px)] max-h-[90dvh] flex flex-col bg-white dark:bg-midnight text-ink dark:text-cloud rounded-xl shadow-lg overflow-hidden " +
          "transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] " +
          (shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2")
        }
      >
        {/* Header */}
        <div className="flex-none flex items-start justify-between gap-3 px-6 pt-5 pb-4 border-b border-[color:var(--color-border)] dark:border-storm">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
              Edit code
            </div>
            <h2
              id={`edit-modal-title-${code.token}`}
              className="font-display font-bold text-[22px] tracking-[-0.02em] text-ink dark:text-cloud truncate"
            >
              {code.display_label}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition bg-transparent border-0 p-1 cursor-pointer flex-shrink-0"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Body — the scroll region when content exceeds the panel's max height */}
        <div className="min-h-0 overflow-y-auto px-6 py-5 grid grid-cols-1 min-[560px]:grid-cols-[200px_1fr] gap-6">
          {/* Left — fixed: preview + short link */}
          <div className="flex flex-col gap-4">
            <LivePreview
              design={{ token: code.token, foregroundColor, backgroundColor, ...gradient, frameStyle, headerText: text.headerText, footerText: text.footerText, textWeight: text.textWeight, moduleShape, eyeShape, logo: logoFile, logoSize, removeLogo }}
              maxWidth="max-w-[156px]"
            />
            <div className="bg-cloud dark:bg-coal border border-[color:var(--color-border)] dark:border-storm rounded p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-muted)] dark:text-muted-light">
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Short link · fixed
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <span className="font-mono text-[13px] text-voltage-dark dark:text-voltage-light truncate">{shortDisplay}</span>
                <button
                  type="button"
                  onClick={copyShortLink}
                  aria-label="Copy short link"
                  className="text-[color:var(--color-muted)] dark:text-muted-light hover:text-coal dark:hover:text-cloud transition bg-transparent border-0 p-0.5 cursor-pointer flex-shrink-0"
                >
                  {copied ? (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-voltage-dark dark:text-voltage-light" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  )}
                </button>
              </div>
              {/* Beside the lock wall, "repoint it anytime" said the opposite of
                  the wall (#336). Locked, the line says what stays true. */}
              <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-2 leading-snug">
                This is what the QR encodes. It never changes, so a printed code stays scannable.{" "}
                {locked
                  ? "It keeps opening the link it has now, with or without Pro."
                  : "Restyle or repoint it anytime."}
              </p>
            </div>
          </div>

          {/* Right — editable, or the lock-wall */}
          <div className="min-w-0">
            {locked ? (
              <div className="flex flex-col gap-4">
                <LockWall onUpgrade={onUpgrade} destination={code.destination_url} />
                {/* Removal is not an edit: a person can take their own published
                    details down at any scan count (#332). */}
                {code.contact_page && (
                  <ContactPageRemove token={code.token} onRemoved={(row) => { if (row) onSaved(row); onClose() }} />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {underReview && (
                  <div role="status" className="rounded border border-[color:var(--color-warn)] bg-[color:var(--color-warn)]/10 px-3 py-2.5 text-sm">
                    <span className="font-semibold">Under review.</span>{" "}
                    This destination is on Google's list of unsafe sites, so scans see a review page for now. The code itself is fine.{" "}
                    <ReCheckLink href={code.appeal_url} />
                  </div>
                )}
                <label className="block">
                  <span className="block text-sm font-semibold mb-1.5">Label</span>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    maxLength={80}
                    placeholder={code.display_label}
                    autoFocus
                    className="w-full border border-[color:var(--color-border)] dark:border-storm rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 focus:border-voltage"
                  />
                </label>

                <div>
                  <span id="edit-destination-label" className="block text-sm font-semibold mb-1.5">
                    Destination <span className="font-normal text-[color:var(--color-muted)] dark:text-muted-light">· where it redirects</span>
                  </span>
                  <div className="font-mono text-xs text-[color:var(--color-muted)] dark:text-muted-light mb-1 truncate">{shortDisplay} →</div>
                  <DestinationInput
                    value={destinationUrl}
                    onChange={setDestinationUrl}
                    disabled={underReview}
                    contactPages={contactPages || code.contact_page}
                    hosted={cp.hosted}
                    onHostedChange={cp.setHosted}
                    pickerClassName="disabled:opacity-60 disabled:cursor-not-allowed border border-[color:var(--color-border)] dark:border-storm rounded pl-3 pr-2 py-2 text-sm bg-white dark:bg-midnight focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1"
                    inputClassName="disabled:opacity-60 disabled:cursor-not-allowed w-full border border-[color:var(--color-border)] dark:border-storm rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-voltage focus:ring-offset-1 focus:border-voltage"
                    inputProps={{ "aria-labelledby": "edit-destination-label" }}
                  />
                  {cp.hosted && (
                    <div className="mt-3 flex flex-col gap-3">
                      {cp.ready ? (
                        <ContactPageFields page={cp.page} onChange={cp.setPage} errors={cp.errors} disabled={underReview || saving} compact />
                      ) : cp.failed ? (
                        <p className="text-sm text-fail" role="alert">Couldn't load the page's details. Close this and try again.</p>
                      ) : (
                        <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light">Loading the page's details…</p>
                      )}
                    </div>
                  )}
                  {cp.exists && (
                    <div className="mt-3 flex flex-col">
                      <ContactPageRemove token={code.token} disabled={saving} onRemoved={(row) => { if (row) onSaved(row); onClose() }} />
                    </div>
                  )}
                </div>

                <div>
                  <span className="block text-sm font-semibold mb-2">Color</span>
                  <ColorPicker
                    foreground={foregroundColor}
                    background={backgroundColor}
                    onChange={({ foreground, background }) => {
                      setForegroundColor(foreground)
                      setBackgroundColor(background)
                    }}
                  />
                  <div className="mt-3">
                    <GradientPicker
                      foreground={foregroundColor}
                      background={backgroundColor}
                      gradientColor={gradient.gradientColor}
                      gradientType={gradient.gradientType}
                      onChange={setGradient}
                    />
                  </div>
                </div>

                <div>
                  <span className="block text-sm font-semibold mb-2">Logo</span>
                  <LogoUpload
                    file={logoFile}
                    previewUrl={removeLogo ? null : code.logo_url}
                    onChange={(file) => { setLogoFile(file); setRemoveLogo(false) }}
                    onRemove={() => { setLogoFile(null); setRemoveLogo(true) }}
                  />
                  {!!(logoFile || code.logo_url) && !removeLogo && (
                    <LogoSizeSelect value={logoSize} onChange={setLogoSize} />
                  )}
                </div>

                <div>
                  <span className="block text-sm font-semibold mb-2">Frame</span>
                  <FrameSelect value={frameStyle} onChange={setFrameStyle} />
                </div>

                <div>
                  <span className="block text-sm font-semibold mb-2">Text</span>
                  <TextFields design={text} setDesign={setText} idPrefix={`qr-text-edit-${code.token}`} />
                </div>

                <div>
                  <span className="flex items-center gap-2 text-sm font-semibold mb-2">
                    Shape
                    {!isPaid && (
                      <span className="inline-flex items-center rounded-full bg-plasma px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Pro</span>
                    )}
                  </span>
                  <ShapeSelect
                    moduleShape={moduleShape}
                    eyeShape={eyeShape}
                    locked={!isPaid}
                    onUpgrade={onUpgrade}
                    onChange={({ moduleShape: m, eyeShape: e }) => { setModuleShape(m); setEyeShape(e) }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer (hidden in the locked state — nothing to save) */}
        {!locked && (
          <div className="flex-none flex items-center justify-between gap-3 px-6 py-4 border-t border-[color:var(--color-border)] dark:border-storm">
            <div className="flex flex-col">
              <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">
                {dirty ? "Unsaved changes" : "No changes"}
              </span>
              {error && (
                <span className="text-xs text-fail mt-0.5" role="alert">
                  {error}
                  {appealUrl && <> <ReCheckLink href={appealUrl} /></>}
                  <MailtoSuggestion
                    suggestion={suggestion}
                    onApply={(next) => { setDestinationUrl(next); setSuggestion(null); setError(null) }}
                  />
                </span>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-[color:var(--color-muted)] dark:text-muted-light rounded px-3 py-2 hover:text-ink dark:hover:text-cloud transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="bg-voltage text-coal rounded-lg px-4 py-2 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </dialog>
  )
}

// Free-tier, past-the-paywall state: editing is locked but scans keep counting
// and unlock is retroactive on upgrade. Plasma is the portfolio's Pro/upgrade
// accent — used here, never on a primary CTA. The button opens the pricing box
// with the code's destination, so the box can say where the code still goes
// (#336).
function LockWall({ onUpgrade, destination }) {
  return (
    <div className="flex flex-col items-center text-center gap-3 py-4 px-2">
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-plasma-pale dark:bg-plasma/20 text-plasma dark:text-plasma-light">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </span>
      <div className="font-display font-bold text-lg text-ink dark:text-cloud">Editing is locked</div>
      <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light leading-snug">
        This code passed its free 50-scan window. Upgrade to Pro to restyle it and change where it points. Your scans keep counting either way, and unlocking is retroactive.
      </p>
      <button
        type="button"
        onClick={() => (onUpgrade ? onUpgrade("locked", { destination }) : window.location.assign("/pricing"))}
        className="w-full bg-plasma text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-plasma-dark transition mt-1"
      >
        Upgrade to Pro
      </button>
    </div>
  )
}
